import React, { useState } from "react";
import { Row, Col, FormGroup, Label, Input, Button, FormFeedback, Tooltip } from "reactstrap";
import { useTranslation } from "react-i18next";
import { Formik, Form as FormikForm, Field, ErrorMessage } from "formik";
import * as yup from "yup";
import { useListQuery } from "helpers/http-client/hooks/useCrud";
import { API_ENDPOINTS } from "helpers/http-client/httpConstants";
import { AddMedicineFormData } from "../medicineTypes";

interface PackageOption {
  packageSizeId: string;
  description: string;
  unitCount?: number;
  unitType?: string;
}

const validationSchema = yup.object({
  piecesInPacking: yup
    .number()
    .typeError("Pieces in Packing must be a number")
    .required("Pieces in Packing is required"),
  purchasePrice: yup
    .number()
    .typeError("Purchase Price must be a number")
    .required("Purchase Price is required"),
  salesPrice: yup
    .number()
    .typeError("Sales Price must be a number")
    .required("Sales Price is required"),
  retailPrice: yup
    .number()
    .typeError("Retail Price must be a number")
    .required("Retail Price is required"),
  quantity: yup
    .number()
    .typeError("Quantity must be a number")
    .required("Quantity is required"),
  maxSaleDiscount: yup
    .number()
    .typeError("Max Sale Discount must be a number")
    .min(0, "Max Sale Discount cannot be negative"),
});

interface InventoryPricingFormProps {
  formData: AddMedicineFormData;
  onNext: (values: Partial<AddMedicineFormData>) => void;
  onBack: () => void;
  onSkip: () => void;
  onSkipAll: () => void;
  isUrdu: boolean;
}

export const InventoryPricingForm: React.FC<InventoryPricingFormProps> = ({
  formData,
  onNext,
  onBack,
  onSkip,
  onSkipAll,
  isUrdu,
}) => {
  const { t } = useTranslation();
  const [tooltipOpen, setTooltipOpen] = useState<{ [key: string]: boolean }>({});
  const { data: packages = [], isLoading: isLoadingPackages } = useListQuery<PackageOption>(
    API_ENDPOINTS.PACKAGE_DETAILS
  );

  const toggleTooltip = (field: string) =>
    setTooltipOpen((prev) => ({ ...prev, [field]: !prev[field] }));

  return (
    <Formik
      initialValues={{
        piecesInPacking: formData.piecesInPacking || "",
        purchasePrice: formData.purchasePrice || "",
        salesPrice: formData.salesPrice || "",
        retailPrice: formData.retailPrice || "",
        packSalesTax: formData.packSalesTax || "",
        quantity: formData.quantity || "",
        batchNumber: formData.batchNumber || "",
        expiryDate: formData.expiryDate || "",
        currency: formData.currency || "",
        country: formData.country || "",
        packageSizeId: formData.packageSizeId || "",
        maxSaleDiscount: formData.maxSaleDiscount || "",
      }}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        const parsedValues: Partial<AddMedicineFormData> = {
          ...values,
          piecesInPacking: values.piecesInPacking ? parseInt(values.piecesInPacking as any, 10) : undefined,
          purchasePrice: values.purchasePrice ? parseFloat(values.purchasePrice as any) : undefined,
          salesPrice: values.salesPrice ? parseFloat(values.salesPrice as any) : undefined,
          retailPrice: values.retailPrice ? parseFloat(values.retailPrice as any) : undefined,
          packSalesTax: values.packSalesTax ? parseFloat(values.packSalesTax as any) : undefined,
          quantity: values.quantity ? parseInt(values.quantity as any, 10) : undefined,
          maxSaleDiscount: values.maxSaleDiscount ? parseFloat(values.maxSaleDiscount as any) : undefined,
        };
        onNext(parsedValues);
      }}
      enableReinitialize
    >
      {({ errors, touched, isValid, dirty, values }) => (
        <FormikForm dir={isUrdu ? "rtl" : "ltr"} className="p-3">
          <Row>
            <Col lg={6}>
              <FormGroup className="mb-3">
                <Label htmlFor="piecesInPacking" className={isUrdu ? "fw-bold" : ""}>
                  {t("Pieces in Packing")} *{" "}
                  <span id="piecesTooltip" className="text-info">?</span>
                </Label>
                <Tooltip
                  placement="top"
                  isOpen={tooltipOpen.piecesInPacking}
                  target="piecesTooltip"
                  toggle={() => toggleTooltip("piecesInPacking")}
                >
                  {t("Number of units in one package, e.g., 10 tablets.")}
                </Tooltip>
                <Field
                  name="piecesInPacking"
                  as={Input}
                  type="number"
                  placeholder={t("e.g., 10")}
                  invalid={touched.piecesInPacking && !!errors.piecesInPacking}
                  style={{ textAlign: isUrdu ? "right" : "left" }}
                  className="form-control"
                />
                <ErrorMessage
                  name="piecesInPacking"
                  component={FormFeedback}
                  className={isUrdu ? "fw-bold" : ""}
                />
              </FormGroup>
            </Col>
            <Col lg={6}>
              <FormGroup className="mb-3">
                <Label htmlFor="quantity" className={isUrdu ? "fw-bold" : ""}>
                  {t("Quantity")} * <span id="quantityTooltip" className="text-info">?</span>
                </Label>
                <Tooltip
                  placement="top"
                  isOpen={tooltipOpen.quantity}
                  target="quantityTooltip"
                  toggle={() => toggleTooltip("quantity")}
                >
                  {t("Number of packages in stock.")}
                </Tooltip>
                <Field
                  name="quantity"
                  as={Input}
                  type="number"
                  placeholder={t("e.g., 50")}
                  invalid={touched.quantity && !!errors.quantity}
                  style={{ textAlign: isUrdu ? "right" : "left" }}
                  className="form-control"
                />
                <ErrorMessage
                  name="quantity"
                  component={FormFeedback}
                  className={isUrdu ? "fw-bold" : ""}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col lg={4}>
              <FormGroup className="mb-3">
                <Label htmlFor="purchasePrice" className={isUrdu ? "fw-bold" : ""}>
                  {t("Purchase Price")} *{" "}
                  <span id="purchasePriceTooltip" className="text-info">?</span>
                </Label>
                <Tooltip
                  placement="top"
                  isOpen={tooltipOpen.purchasePrice}
                  target="purchasePriceTooltip"
                  toggle={() => toggleTooltip("purchasePrice")}
                >
                  {t("Cost of purchasing one package.")}
                </Tooltip>
                <Field
                  name="purchasePrice"
                  as={Input}
                  type="number"
                  placeholder={t("e.g., 400")}
                  invalid={touched.purchasePrice && !!errors.purchasePrice}
                  style={{ textAlign: isUrdu ? "right" : "left" }}
                  className="form-control"
                />
                <ErrorMessage
                  name="purchasePrice"
                  component={FormFeedback}
                  className={isUrdu ? "fw-bold" : ""}
                />
              </FormGroup>
            </Col>
            <Col lg={4}>
              <FormGroup className="mb-3">
                <Label htmlFor="salesPrice" className={isUrdu ? "fw-bold" : ""}>
                  {t("Sales Price")} *{" "}
                  <span id="salesPriceTooltip" className="text-info">?</span>
                </Label>
                <Tooltip
                  placement="top"
                  isOpen={tooltipOpen.salesPrice}
                  target="salesPriceTooltip"
                  toggle={() => toggleTooltip("salesPrice")}
                >
                  {t("Price at which the package is sold.")}
                </Tooltip>
                <Field
                  name="salesPrice"
                  as={Input}
                  type="number"
                  placeholder={t("e.g., 500")}
                  invalid={touched.salesPrice && !!errors.salesPrice}
                  style={{ textAlign: isUrdu ? "right" : "left" }}
                  className="form-control"
                />
                <ErrorMessage
                  name="salesPrice"
                  component={FormFeedback}
                  className={isUrdu ? "fw-bold" : ""}
                />
              </FormGroup>
            </Col>
            <Col lg={4}>
              <FormGroup className="mb-3">
                <Label htmlFor="retailPrice" className={isUrdu ? "fw-bold" : ""}>
                  {t("Retail Price")} *{" "}
                  <span id="retailPriceTooltip" className="text-info">?</span>
                </Label>
                <Tooltip
                  placement="top"
                  isOpen={tooltipOpen.retailPrice}
                  target="retailPriceTooltip"
                  toggle={() => toggleTooltip("retailPrice")}
                >
                  {t("Recommended retail price for one package.")}
                </Tooltip>
                <Field
                  name="retailPrice"
                  as={Input}
                  type="number"
                  placeholder={t("e.g., 550")}
                  invalid={touched.retailPrice && !!errors.retailPrice}
                  style={{ textAlign: isUrdu ? "right" : "left" }}
                  className="form-control"
                />
                <ErrorMessage
                  name="retailPrice"
                  component={FormFeedback}
                  className={isUrdu ? "fw-bold" : ""}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col lg={4}>
              <FormGroup className="mb-3">
                <Label htmlFor="packSalesTax" className={isUrdu ? "fw-bold" : ""}>
                  {t("Pack Sales Tax")} <span id="taxTooltip" className="text-info">?</span>
                </Label>
                <Tooltip
                  placement="top"
                  isOpen={tooltipOpen.packSalesTax}
                  target="taxTooltip"
                  toggle={() => toggleTooltip("packSalesTax")}
                >
                  {t("Sales tax percentage for the package.")}
                </Tooltip>
                <Field
                  name="packSalesTax"
                  as={Input}
                  type="number"
                  placeholder={t("e.g., 5")}
                  style={{ textAlign: isUrdu ? "right" : "left" }}
                  className="form-control"
                />
              </FormGroup>
            </Col>
            <Col lg={4}>
              <FormGroup className="mb-3">
                <Label htmlFor="currency" className={isUrdu ? "fw-bold" : ""}>
                  {t("Currency")} <span id="currencyTooltip" className="text-info">?</span>
                </Label>
                <Tooltip
                  placement="top"
                  isOpen={tooltipOpen.currency}
                  target="currencyTooltip"
                  toggle={() => toggleTooltip("currency")}
                >
                  {t("Currency for pricing, e.g., PKR, USD.")}
                </Tooltip>
                <Field
                  name="currency"
                  as={Input}
                  placeholder={t("e.g., PKR")}
                  style={{ textAlign: isUrdu ? "right" : "left" }}
                  className="form-control"
                />
              </FormGroup>
            </Col>
            <Col lg={4}>
              <FormGroup className="mb-3">
                <Label htmlFor="country" className={isUrdu ? "fw-bold" : ""}>
                  {t("Country")} <span id="countryTooltip" className="text-info">?</span>
                </Label>
                <Tooltip
                  placement="top"
                  isOpen={tooltipOpen.country}
                  target="countryTooltip"
                  toggle={() => toggleTooltip("country")}
                >
                  {t("Country where the medicine is sold.")}
                </Tooltip>
                <Field
                  name="country"
                  as={Input}
                  placeholder={t("e.g., Pakistan")}
                  style={{ textAlign: isUrdu ? "right" : "left" }}
                  className="form-control"
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col lg={6}>
              <FormGroup className="mb-3">
                <Label htmlFor="batchNumber" className={isUrdu ? "fw-bold" : ""}>
                  {t("Batch Number")} <span id="batchTooltip" className="text-info">?</span>
                </Label>
                <Tooltip
                  placement="top"
                  isOpen={tooltipOpen.batchNumber}
                  target="batchTooltip"
                  toggle={() => toggleTooltip("batchNumber")}
                >
                  {t("Batch code for traceability, e.g., BATCH-123.")}
                </Tooltip>
                <Field
                  name="batchNumber"
                  as={Input}
                  placeholder={t("e.g., BATCH-123")}
                  style={{ textAlign: isUrdu ? "right" : "left" }}
                  className="form-control"
                />
              </FormGroup>
            </Col>
            <Col lg={6}>
              <FormGroup className="mb-3">
                <Label htmlFor="expiryDate" className={isUrdu ? "fw-bold" : ""}>
                  {t("Expiry Date")} <span id="expiryTooltip" className="text-info">?</span>
                </Label>
                <Tooltip
                  placement="top"
                  isOpen={tooltipOpen.expiryDate}
                  target="expiryTooltip"
                  toggle={() => toggleTooltip("expiryDate")}
                >
                  {t("Expiration date of the batch.")}
                </Tooltip>
                <Field
                  name="expiryDate"
                  as={Input}
                  type="date"
                  style={{ textAlign: isUrdu ? "right" : "left" }}
                  className="form-control"
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col lg={6}>
              <FormGroup className="mb-3">
                <Label htmlFor="maxSaleDiscount" className={isUrdu ? "fw-bold" : ""}>
                  {t("Max Sale Discount (%)")} <span id="maxSaleDiscountTooltip" className="text-info">?</span>
                </Label>
                <Tooltip
                  placement="top"
                  isOpen={tooltipOpen.maxSaleDiscount}
                  target="maxSaleDiscountTooltip"
                  toggle={() => toggleTooltip("maxSaleDiscount")}
                >
                  {t("Maximum discount percentage allowed on sales.")}
                </Tooltip>
                <Field
                  name="maxSaleDiscount"
                  as={Input}
                  type="number"
                  placeholder={t("e.g., 10")}
                  invalid={touched.maxSaleDiscount && !!errors.maxSaleDiscount}
                  style={{ textAlign: isUrdu ? "right" : "left" }}
                  className="form-control"
                />
                <ErrorMessage
                  name="maxSaleDiscount"
                  component={FormFeedback}
                  className={isUrdu ? "fw-bold" : ""}
                />
              </FormGroup>
            </Col>
          </Row>
          <div className="d-flex justify-content-between mt-4">
            <div>
              <Button color="secondary" onClick={onBack} className={isUrdu ? "fw-bold" : "me-2"}>
                {t("Back")}
              </Button>
              <Button color="warning" onClick={onSkip} className={isUrdu ? "fw-bold" : ""}>
                {t("Skip")}
              </Button>
            </div>
            <div>
              <Button color="secondary" onClick={onSkipAll} className={isUrdu ? "fw-bold" : "me-2"}>
                {t("Skip All")}
              </Button>
              <Button
                type="submit"
                color="primary"
                disabled={!(isValid && dirty)}
                className={isUrdu ? "fw-bold" : ""}
              >
                {t("Next")}
              </Button>
            </div>
          </div>
        </FormikForm>
      )}
    </Formik>
  );
};